import { hubPagesInput } from '../api/v2PrefetchInputs';
import { trpc } from '../../lib/trpc';
import { useCurrentUser } from '../useCurrentUser';
import { usePortalBrandContext } from '../usePortalBrandContext';

/** Shown by the agent instead of sending when no single brand is selected. */
export const HUB_NO_BRAND_TEXT = 'Pick a brand first. The agent answers for one brand at a time.';

/**
 * The brand every hub call and the agent chat are scoped to: the brand the
 * page is showing, as Brand.snowflakeIdNum (the id the page's own procedures
 * send). Null when no single brand is selected (an admin on "All brands"):
 * the server refuses hub calls without a brand, so callers skip them.
 */
export function useHubBrandId(): number | null {
  return usePortalBrandContext().snowflakeIdNum;
}

/**
 * Returns whether the current user's brand has Brand Hub enabled.
 * Drives nav visibility — gates all Hub UI behind this check.
 */
export function useHubEnabled(): boolean {
  const { user } = useCurrentUser();
  return Boolean(user?.brand?.hubEnabled);
}

/**
 * Fetch all Hub pages for the current brand.
 * @param enabled - pass false to skip the query (e.g. when Hub is not enabled)
 */
export function useHubPages(enabled = true) {
  const brandId = useHubBrandId();
  return trpc.hub.listPages.useQuery(hubPagesInput(brandId), {
    enabled: enabled && brandId != null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch a single Hub page by ID.
 * Guarded against NaN from route params (page renders before params resolve).
 */
export function useHubPage(pageId: number) {
  const brandId = useHubBrandId();
  return trpc.hub.getPage.useQuery(
    { pageId, brandId: brandId ?? undefined },
    { enabled: !Number.isNaN(pageId) && brandId != null, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  );
}

/**
 * Run a widget and return its computed output.
 * Results are cached for 5 minutes; retried once on failure.
 */
export function useRunWidget(widgetId: number) {
  const brandId = useHubBrandId();
  return trpc.hub.runWidget.useQuery(
    { widgetId, brandId: brandId ?? undefined },
    {
      enabled: !Number.isNaN(widgetId) && brandId != null,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
}

/**
 * Mutation to save (create or update) a widget.
 * On success, invalidates listPages and getPage queries so UI stays fresh.
 */
export function useSaveWidget() {
  const utils = trpc.useUtils();
  return trpc.hub.saveWidget.useMutation({
    onSuccess: () => {
      utils.hub.listPages.invalidate();
      utils.hub.getPage.invalidate();
    },
  });
}
